import { v4 as uuidV4 } from 'uuid';
import { isAxiosError } from 'axios';
import { http } from '@/api/http';
import { ApiResponseBody, PaginatedApiResponseBody, Uuid } from '@/api/types';
import {
  getCaseApiUrl,
  Case,
  CaseStatus,
  CaseStatusCounts,
  NewCase,
  CaseLog,
  DeclineCaseParams,
  ClinicalDataItem,
  PaginatedCaseListFilters,
  NRPType,
  WOLSType,
  QuoteDeclineReason,
  CaseActivityLog,
  TimestampWithZone,
  PerfusionTerminationOption,
  DraftCase,
  CaseDuplicateType,
  ArchivedCase,
  CaseUserGroup,
  CloseCaseParams,
  ArchiveCloseCaseResponse,
  ReallocationCandidate,
  ReallocationCandidatesPayload,
  BulkAssignmentCase,
  PaginatedBulkAssignmentCasesPayload,
  BulkAssignmentStatusData,
  BulkAssignmentPayloadOperation,
  StartBulkAssignmentResult,
  TxCenterORData,
  CaseAccessTicketsListResponse,
  GrantAccessBody,
  SupplementalOrganRecord,
  SupplementalOrganType,
  SetAssignedCSLabelBody,
} from '@/api/apis/case';
import { opoCaseApiFetcher } from './opo-case';

const getCaseList = async (params: PaginatedCaseListFilters) => {
  const res = await http.get<PaginatedApiResponseBody<Case>>(getCaseApiUrl.list(), {
    params,
    paramsSerializer: { indexes: true },
  });
  return res.data.data;
};

const getCaseStatusCounts = async (params: Omit<PaginatedCaseListFilters, 'itemsPerPage' | 'pageIndex'>) => {
  const res = await http.get<ApiResponseBody<CaseStatusCounts>>(getCaseApiUrl.count(), {
    params,
    paramsSerializer: { indexes: true },
  });
  return res.data.data;
};

const getArchiveCaseList = async (params: PaginatedCaseListFilters) => {
  const res = await http.get<PaginatedApiResponseBody<ArchivedCase>>(getCaseApiUrl.archiveCases(), {
    params,
    paramsSerializer: { indexes: null },
  });
  return res.data.data;
};

const getBulkAssignmentCasesList = async (params: PaginatedBulkAssignmentCasesPayload) => {
  const res = await http.get<PaginatedApiResponseBody<BulkAssignmentCase>>(getCaseApiUrl.bulkAssignmentCases(), {
    params,
    paramsSerializer: { indexes: null },
  });
  return res.data.data;
};

const getBulkAssignmentStatus = async (): Promise<BulkAssignmentStatusData | null> => {
  try {
    const res = await http.get<ApiResponseBody<BulkAssignmentStatusData>>(getCaseApiUrl.bulkAssignmentStatus());
    return res.data.data;
  } catch (error) {
    // The endpoint answers 404 (BulkOperationNoActiveOperationError) when no saga is in progress.
    // That is the "nothing is running" signal, so map it to null instead of rejecting.
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

const patchBulkAssignment = async (payload: BulkAssignmentPayloadOperation[]) => {
  const res = await http.patch<ApiResponseBody<StartBulkAssignmentResult>>(
    getCaseApiUrl.bulkAssignmentAction(),
    payload,
  );
  return res.data.data;
};

const getReallocationCandidates = async (params: ReallocationCandidatesPayload) => {
  const res = await http.get<ApiResponseBody<ReallocationCandidate[]>>(getCaseApiUrl.reallocationCandidates(), {
    params,
  });
  return res.data.data;
};

const getDuplicatesList = async () => {
  const res = await http.get<PaginatedApiResponseBody<CaseDuplicateType>>(getCaseApiUrl.duplicatesList());
  return res.data.data;
};

const getCaseById = async (caseId: string): Promise<Case | DraftCase> => {
  const res = await http.get<ApiResponseBody<Case | DraftCase>>(getCaseApiUrl.caseById(caseId));
  return res.data.data;
};

export type CreateDraftCaseType = {
  caseId: Uuid;
  donorId: string;
} & Omit<Partial<NewCase>, 'donorId'>;
const createDraftCase = async (data: CreateDraftCaseType) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.createDraftCase(), data);
  return res.data.data;
};

const createClinicalScreeningCase = async (data: CreateDraftCaseType) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.createClinicalScreeningCase(), data);
  return res.data.data;
};

const createReallocationDraft = async (data: CreateDraftCaseType) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.createReallocationDraft(), data);
  return res.data.data;
};

const createReallocationClinicalScreening = async (data: CreateDraftCaseType) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.createReallocationClinicalScreening(), data);
  return res.data.data;
};

export type CreateTxCenterDraftCaseType = {
  caseId: Uuid;
  donorId: string;
} & Omit<Partial<NewCase>, 'donorId'>;
const createTxCenterDraftCase = async (data: CreateTxCenterDraftCaseType) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.createTxCenterDraftCase(), data);
  return res.data.data;
};

const deleteDraftCase = async (caseId: string) => {
  const res = await http.delete<ApiResponseBody<Case>>(getCaseApiUrl.deleteDraftCase(caseId));
  return res.data.data;
};

export type UpdateCaseRequestBody = {
  status: CaseStatus;
};
const changeCaseStatus = async (caseId: string, status: CaseStatus) => {
  const body: UpdateCaseRequestBody = { status };
  const res = await http.put<ApiResponseBody<Case>>(getCaseApiUrl.caseUpdate(caseId), body);
  return res.data.data;
};

const changeStatusClinicalScreening = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusClinicalScreening(caseId));
  return res.data.data;
};

const changeStatusAccepted = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusAccepted(caseId));
  return res.data.data;
};

const changeStatusPendingLogistics = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusPendingLogistics(caseId));
  return res.data.data;
};

const changeStatusResourcesBooked = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusResourcesBooked(caseId));
  return res.data.data;
};

const changeStatusAtDonorHospital = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusAtDonorHospital(caseId));
  return res.data.data;
};

const changeStatusOnOcs = async (caseId: string, data?: TimestampWithZone) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusOnOcs(caseId), data ?? {});
  return res.data.data;
};

const changeStatusDeployedToTxCenter = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusDeployedToTxCenter(caseId));
  return res.data.data;
};

const changeStatusAtTxCenter = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusAtTxCenter(caseId));
  return res.data.data;
};

type LungsPerfusionTerminationPayload = {
  perfusionTerminationDataItem?: PerfusionTerminationOption;
};

export type OffOCSPayload = LungsPerfusionTerminationPayload | TimestampWithZone;

const changeStatusOffOcs = async (caseId: string, data?: OffOCSPayload) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusOffOcs(caseId), data ?? {});
  return res.data.data;
};

const changeStatusReturningToBase = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusReturningToBase(caseId));
  return res.data.data;
};

const changeStatusCaseFinished = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusCaseFinished(caseId));
  return res.data.data;
};

const changeStatusCaseClosed = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusCaseClosed(caseId));
  return res.data.data;
};

const changeStatusCasesClosed = async (body: CloseCaseParams) => {
  const res = await http.post<ApiResponseBody<ArchiveCloseCaseResponse>>(getCaseApiUrl.statusCasesClosed(), body);
  return res.data.data;
};

const changeStatusResourcesDeployed = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusResourcesDeployed(caseId));
  return res.data.data;
};

const changeStatusDeclined = async (caseId: string, dispositionComment: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusDeclined(caseId), { dispositionComment });
  return res.data.data;
};

const changeStatusServiceDeclined = async (caseId: string, body: DeclineCaseParams) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusServiceDeclined(caseId), body);
  return res.data.data;
};

const changeStatusRollbackToServiceDeclined = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusBollbackToServiceDeclined(caseId));
  return res.data.data;
};

const changeToSpecificCaseStatus = async (caseId: string, status: CaseStatus) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.statusChange(caseId), { newStatus: status });
  return res.data.data;
};

const setLightsAndSirensRequest = async (caseId: string, isLightsSirensRequired: boolean) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.setSirensLightsSupport(caseId), {
    isLightsSirensRequired,
  });
  return res.data.data;
};

const setNonStopFlightRequired = async (caseId: string, nonStopFlightRequired: boolean) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.setNonStopFlightRequired(caseId), {
    nonStopFlightRequired,
  });
  return res.data.data;
};

const setQuestionaryEditAllowanceRequest = async (caseId: string, isQuestionaryEditAllowed: boolean) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.setQuestionaryEditAllowance(caseId), {
    isQuestionaryEditAllowed,
  });
  return res.data.data;
};

const setRouteWaitingForAviationApproval = async (caseId: string, comment?: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.waitingAviationApproval(caseId), { comment });
  return res.data.data;
};

const setRouteWaitingForQuoteApproval = async (caseId: string, comment?: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.waitingForQuoteApproval(caseId), { comment });
  return res.data.data;
};

const setRouteApproveAviation = async (caseId: string, comment?: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.approveAviation(caseId), {
    comment,
    createMaoTrips: true,
  });
  return res.data.data;
};

const setRouteRejectAviation = async (caseId: string, comment?: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.rejectAviation(caseId), {
    comment,
  });
  return res.data.data;
};

const setRouteApproveGround = async (caseId: string, comment?: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.approveGround(caseId), { comment });
  return res.data.data;
};

const setRouteQuoteApprove = async (caseId: string, comment?: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.approveQuoteInternally(caseId), { comment });
  return res.data.data;
};

const setRouteQuoteSentToTxReview = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.quoteSentToTxReview(caseId));
  return res.data.data;
};

const setRoutePlanning = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.routePlanningStatus(caseId));
  return res.data.data;
};

const setRouteBookingPending = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.bookingPendingStatus(caseId));
  return res.data.data;
};

const setRouteResourceBooked = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.resourceBookedRouteStatus(caseId));
  return res.data.data;
};

const setRoutePlanningDone = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.routePlanningDoneStatus(caseId));
  return res.data.data;
};

const updateCase = async (caseId: string, data: Partial<Case>) => {
  const res = await http.patch<ApiResponseBody<Case>>(getCaseApiUrl.caseUpdate(caseId), data);
  return res.data.data;
};

const updateReallocated = async (caseId: string, data: Partial<Case>) => {
  const res = await http.patch<ApiResponseBody<Case>>(getCaseApiUrl.updateReallocated(caseId), data);
  return res.data.data;
};

const updateTxCenterOR = async (caseId: Uuid, data: Partial<TxCenterORData>) => {
  const res = await http.patch<ApiResponseBody<Case>>(getCaseApiUrl.updateTxCenterOR(caseId), data);
  return res.data.data;
};

export type AddCaseNoteRequestBody = {
  message: string;
};
const addCaseNote = async (caseId: string, body: AddCaseNoteRequestBody) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.caseNotes(caseId), body);
  return res.data.data;
};

export type UpdateCaseNoteRequestBody = {
  message: string;
};
const updateCaseNote = async (caseId: string, noteId: string, body: UpdateCaseNoteRequestBody) => {
  const res = await http.patch<ApiResponseBody<Case>>(getCaseApiUrl.caseNoteById(caseId, noteId), body);
  return res.data.data;
};

const deleteCaseNote = async (caseId: string, noteId: string) => {
  const res = await http.delete<ApiResponseBody<Case>>(getCaseApiUrl.caseNoteById(caseId, noteId));
  return res.data.data;
};

const pinCaseNote = async (caseId: string, noteId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.pinCaseNote(caseId, noteId));
  return res.data.data;
};

const unpinCaseNote = async (caseId: string, noteId: string) => {
  const res = await http.delete<ApiResponseBody<Case>>(getCaseApiUrl.unpinCaseNote(caseId, noteId));
  return res.data.data;
};

const getCaseLogs = async (caseId: string) => {
  const res = await http.get<ApiResponseBody<CaseLog[]>>(getCaseApiUrl.caseLogs(caseId));
  return res.data.data;
};

const getCaseActivityLog = async (caseId: string) => {
  const res = await http.get<ApiResponseBody<CaseActivityLog>>(getCaseApiUrl.caseActivityLog(caseId));
  return res.data.data;
};

export type AssignUserToGroupType = {
  action: 'assign' | 'unassign';
  group: CaseUserGroup;
  employeeId?: Uuid;
  customerId?: Uuid;
  isPrimary?: boolean;
  isDefaultTxUser?: boolean;
};

const assignUsersToGroup = async (caseId: string, payload: AssignUserToGroupType[]) => {
  const res = await http.patch<ApiResponseBody<Case>>(getCaseApiUrl.assignUsers(caseId), payload);
  return res.data.data;
};

export type OpoContactWithPrimary = {
  opoContactId: string;
  isPrimary?: boolean;
  primaryPhone?: string;
};

const assignOpoContacts = async (caseId: string, contacts: OpoContactWithPrimary[]) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.assignOpoContacts(caseId), {
    opoContactIdIsPrimaryPairs: contacts,
  });
  return res.data.data;
};

const removeOpoContacts = async (caseId: string, opoContactIds: string[]) => {
  const res = await http.delete<ApiResponseBody<Case>>(getCaseApiUrl.assignOpoContacts(caseId), {
    data: { opoContactIds },
  });
  return res.data.data;
};

const approveQuoteByTx = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.approveQuoteByTx(caseId), {});
  return res.data.data;
};

export type QuoteRejectRequestBody = {
  comment: string;
  reason: QuoteDeclineReason;
};

const rejectQuoteByTx = async (caseId: string, body: QuoteRejectRequestBody) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.rejectQuoteByTx(caseId), body);
  return res.data.data;
};

const declineOrgan = async (caseId: string, body: { timeZone: string; declineReason: string }) => {
  const res = await http.post(getCaseApiUrl.declineOrgan(caseId), body);
  return res.data.data;
};

const approveOrgan = async (caseId: string, body: { timeZone: string }) => {
  const res = await http.post(getCaseApiUrl.approveOrgan(caseId), body);
  return res.data.data;
};

const declineService = async (caseId: string, body: { comment: string | null }) => {
  const res = await http.post(getCaseApiUrl.declineService(caseId), body);
  return res.data.data;
};

const approveService = async (caseId: string, body: { comment: string | null }) => {
  const res = await http.post(getCaseApiUrl.approveService(caseId), body);
  return res.data.data;
};

const addFavorite = async (caseId: string) => {
  const res = await http.put<ApiResponseBody<Case>>(getCaseApiUrl.favorite(caseId));
  return res.data.data;
};

const removeFavorite = async (caseId: string) => {
  const res = await http.delete<ApiResponseBody<Case>>(getCaseApiUrl.favorite(caseId));
  return res.data.data;
};

export type LinkBody = {
  url: string;
  description: string;
};

const postLink = async (caseId: string, body: LinkBody) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.addLink(caseId), body);
  return res.data.data;
};

const patchLink = async (caseId: string, linkId: string, body: LinkBody) => {
  const res = await http.patch<ApiResponseBody<Case>>(getCaseApiUrl.changeLink(caseId, linkId), body);
  return res.data.data;
};

const deleteLink = async (caseId: string, linkId: string) => {
  const res = await http.delete<ApiResponseBody<Case>>(getCaseApiUrl.changeLink(caseId, linkId));
  return res.data.data;
};

export const updateSupplementalOrgan = async (
  caseId: string,
  kidney: boolean | undefined,
  pancreas: boolean,
  prevOrgansRecord?: SupplementalOrganRecord,
) => {
  const timestamp = new Date().toISOString();
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const details = 'Update supplemental organ';

  const getRecordId = (prevOrgan: SupplementalOrganType) => {
    const record = prevOrgansRecord?.items?.find((item) => item.organ === prevOrgan);
    return record?.id ?? uuidV4();
  };

  const items = [
    ...(kidney !== undefined
      ? [
          {
            id: getRecordId(SupplementalOrganType.Kidney),
            timestamp,
            timeZone,
            details,
            organ: SupplementalOrganType.Kidney,
            yesNo: kidney,
          },
        ]
      : []),
    {
      id: getRecordId(SupplementalOrganType.Pancreas),
      timestamp,
      timeZone,
      details,
      organ: SupplementalOrganType.Pancreas,
      yesNo: pancreas,
    },
  ];

  const body = { items };

  const res = await http.post<ApiResponseBody<ClinicalDataItem>>(getCaseApiUrl.changeSupplementalOrgan(caseId), body);
  return res.data.data;
};

export const updateWols = async (
  caseId: string,
  text: string | null,
  wolsType: WOLSType,
  recordId: string | undefined,
) => {
  const timestamp = new Date().toISOString();
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const body = {
    item: { id: recordId ?? uuidV4(), timestamp, timeZone, text, answer: wolsType },
  };
  const res = await http.post<ApiResponseBody<ClinicalDataItem>>(getCaseApiUrl.wols(caseId), body);

  return res.data.data;
};

export const updateNrpType = async (caseId: string, nrp: boolean, nrpType: NRPType, recordId: string | undefined) => {
  const timestamp = new Date().toISOString();
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const body = {
    item: { id: recordId ?? uuidV4(), timestamp, timeZone, nrpType, yesNo: nrp },
  };
  const res = await http.post<ApiResponseBody<ClinicalDataItem>>(getCaseApiUrl.nrpType(caseId), body);

  return res.data.data;
};

export const copyClinicalData = async (caseId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.copyClinicalData(), { caseId });

  return res.data.data;
};

const setAssignedCSLabel = async (caseId: string, employeeId: string, body: SetAssignedCSLabelBody) => {
  const res = await http.patch<ApiResponseBody<Case>>(getCaseApiUrl.assignedCsLabel(caseId, employeeId), body);
  return res.data.data;
};

const csDeployedToDonorHospital = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.csDeployedToDonorHospital(caseId, employeeId));
  return res.data.data;
};

const csAtDonor = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.csAtDonor(caseId, employeeId));
  return res.data.data;
};

const csOnOcs = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.csOnOcs(caseId, employeeId));
  return res.data.data;
};

const csDeployedFromDonorHospital = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.csDeployedFromDonorHospital(caseId, employeeId));
  return res.data.data;
};

const csAtTxCenter = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.csAtTxCenter(caseId, employeeId));
  return res.data.data;
};

const csOffOcs = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.csOffOcs(caseId, employeeId));
  return res.data.data;
};

const csDeployedFromTxCenter = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.csDeployedFromTxCenter(caseId, employeeId));
  return res.data.data;
};

const csDone = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.csDone(caseId, employeeId));
  return res.data.data;
};

const surgeonDeployedToDonorHospital = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.surgeonDeployedToDonorHospital(caseId, employeeId));
  return res.data.data;
};

const surgeonAtDonor = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.surgeonAtDonor(caseId, employeeId));
  return res.data.data;
};

const surgeonRecoveryCompleted = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.surgeonRecoveryCompleted(caseId, employeeId));
  return res.data.data;
};

const surgeonDeployedFromDonorHospital = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(
    getCaseApiUrl.surgeonDeployedFromDonorHospital(caseId, employeeId),
  );
  return res.data.data;
};

const surgeonDone = async (caseId: string, employeeId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.surgeonDone(caseId, employeeId));
  return res.data.data;
};

const consoleDeployed = async (caseId: string, consoleId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.consoleDeployed(caseId, consoleId));
  return res.data.data;
};

const consoleDone = async (caseId: string, consoleId: string) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.consoleDone(caseId, consoleId));
  return res.data.data;
};

const accessTickets = async (caseId: string) => {
  const res = await http.get<ApiResponseBody<CaseAccessTicketsListResponse>>(getCaseApiUrl.accessTickets(caseId));
  return res.data.data;
};

const accessTicketsGrant = async (caseId: string, body: GrantAccessBody) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.accessTicketsGrant(caseId), body);
  return res.data.data;
};

const accessTicketsGrantInfinite = async (caseId: string, body: GrantAccessBody) => {
  const res = await http.post<ApiResponseBody<Case>>(getCaseApiUrl.accessTicketsGrantInfinite(caseId), body);
  return res.data.data;
};

const accessTicketsRevoke = async (caseId: string, userId: string) => {
  const res = await http.delete<ApiResponseBody<Case>>(getCaseApiUrl.accessTicketsRevoke(caseId, userId));
  return res.data.data;
};

const accessTicketsRevokeInfinite = async (caseId: string, userId: string) => {
  const res = await http.delete<ApiResponseBody<Case>>(getCaseApiUrl.accessTicketsRevokeInfinite(caseId, userId));
  return res.data.data;
};

export const caseApiFetcher = {
  getCaseList,
  getCaseStatusCounts,
  getArchiveCaseList,
  getReallocationCandidates,
  getBulkAssignmentCasesList,
  getBulkAssignmentStatus,
  patchBulkAssignment,
  getDuplicatesList,
  createDraftCase,
  createTxCenterDraftCase,
  createClinicalScreeningCase,
  createReallocationDraft,
  createReallocationClinicalScreening,
  updateReallocated,
  deleteDraftCase,
  getCaseById,
  changeCaseStatus,
  updateCase,
  updateTxCenterOR,
  addCaseNote,
  updateCaseNote,
  deleteCaseNote,
  pinCaseNote,
  unpinCaseNote,
  getCaseLogs,
  getCaseActivityLog,
  changeStatusClinicalScreening,
  changeStatusAccepted,
  changeStatusPendingLogistics,
  changeStatusResourcesBooked,
  changeStatusResourcesDeployed,
  changeStatusAtDonorHospital,
  changeStatusOnOcs,
  changeStatusDeployedToTxCenter,
  changeStatusAtTxCenter,
  changeStatusOffOcs,
  changeStatusReturningToBase,
  changeStatusCaseFinished,
  changeStatusCaseClosed,
  changeStatusCasesClosed,
  changeStatusDeclined,
  changeStatusServiceDeclined,
  changeStatusRollbackToServiceDeclined,
  changeToSpecificCaseStatus,
  setRouteWaitingForAviationApproval,
  setRouteApproveAviation,
  setRouteRejectAviation,
  setRouteWaitingForQuoteApproval,
  setRouteApproveGround,
  setRouteQuoteApprove,
  setRouteQuoteSentToTxReview,
  setRouteBookingPending,
  setRouteResourceBooked,
  setRoutePlanningDone,
  assignUsersToGroup,
  assignOpoContacts,
  removeOpoContacts,
  approveQuoteByTx,
  rejectQuoteByTx,
  addFavorite,
  removeFavorite,
  postLink,
  patchLink,
  deleteLink,
  updateSupplementalOrgan,
  updateWols,
  updateNrpType,
  copyClinicalData,
  setAssignedCSLabel,
  csDeployedToDonorHospital,
  csAtDonor,
  csOnOcs,
  csDeployedFromDonorHospital,
  csAtTxCenter,
  csOffOcs,
  csDeployedFromTxCenter,
  csDone,
  surgeonDeployedToDonorHospital,
  surgeonAtDonor,
  surgeonRecoveryCompleted,
  surgeonDeployedFromDonorHospital,
  surgeonDone,
  consoleDeployed,
  consoleDone,
  declineOrgan,
  approveOrgan,
  declineService,
  approveService,
  setLightsAndSirensRequest,
  setNonStopFlightRequired,
  setQuestionaryEditAllowanceRequest,
  setRoutePlanning,
  accessTickets,
  accessTicketsGrant,
  accessTicketsGrantInfinite,
  accessTicketsRevoke,
  accessTicketsRevokeInfinite,
  opo: opoCaseApiFetcher,
};
